import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { justificatifService } from '../../services/api';
import { Upload, CreditCard, BookOpen, CheckCircle, X, ArrowLeft, FileText } from 'lucide-react';

const YOUNITED_FLOW_LOGO = 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/fk9u448b_IMG_0370.jpeg';

const IdentityUpload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documentType, setDocumentType] = useState(null); // 'id_card', 'passport', or 'residence_permit'
  const [rectoFile, setRectoFile] = useState(null);
  const [versoFile, setVersoFile] = useState(null);
  const [passportFile, setPassportFile] = useState(null);
  const [residenceRectoFile, setResidenceRectoFile] = useState(null);
  const [residenceVersoFile, setResidenceVersoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [rectoPreview, setRectoPreview] = useState(null);
  const [versoPreview, setVersoPreview] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);
  const [residenceRectoPreview, setResidenceRectoPreview] = useState(null);
  const [residenceVersoPreview, setResidenceVersoPreview] = useState(null);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'recto') {
        setRectoFile(file);
        setRectoPreview(reader.result);
      } else if (type === 'verso') {
        setVersoFile(file);
        setVersoPreview(reader.result);
      } else if (type === 'passport') {
        setPassportFile(file);
        setPassportPreview(reader.result);
      } else if (type === 'residence_recto') {
        setResidenceRectoFile(file);
        setResidenceRectoPreview(reader.result);
      } else if (type === 'residence_verso') {
        setResidenceVersoFile(file);
        setResidenceVersoPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (type) => {
    if (type === 'recto') {
      setRectoFile(null);
      setRectoPreview(null);
    } else if (type === 'verso') {
      setVersoFile(null);
      setVersoPreview(null);
    } else if (type === 'passport') {
      setPassportFile(null);
      setPassportPreview(null);
    } else if (type === 'residence_recto') {
      setResidenceRectoFile(null);
      setResidenceRectoPreview(null);
    } else if (type === 'residence_verso') {
      setResidenceVersoFile(null);
      setResidenceVersoPreview(null);
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      if (documentType === 'id_card') {
        // Upload recto
        if (rectoFile) {
          await justificatifService.upload(user.id, 'justificatif_identite_recto', rectoFile);
        }
        // Upload verso
        if (versoFile) {
          await justificatifService.upload(user.id, 'justificatif_identite_verso', versoFile);
        }
      } else if (documentType === 'passport') {
        // Upload passport
        if (passportFile) {
          await justificatifService.upload(user.id, 'justificatif_identite', passportFile);
        }
      } else if (documentType === 'residence_permit') {
        // Upload titre de séjour recto
        if (residenceRectoFile) {
          await justificatifService.upload(user.id, 'justificatif_identite_recto', residenceRectoFile);
        }
        // Upload titre de séjour verso
        if (residenceVersoFile) {
          await justificatifService.upload(user.id, 'justificatif_identite_verso', residenceVersoFile);
        }
      }

      // Créer le justificatif validé pour marquer l'étape comme complétée
      await justificatifService.createValidated(user.id, 'justificatif_identite');

      // Redirect to success page
      navigate('/client/justificatif-success', { 
        state: { 
          type: 'justificatif_identite',
          title: 'Justificatif d\'identité'
        } 
      });
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Erreur lors de l\'upload. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const canSubmit = () => {
    if (documentType === 'id_card') {
      return rectoFile && versoFile;
    } else if (documentType === 'passport') {
      return passportFile;
    } else if (documentType === 'residence_permit') {
      return residenceRectoFile && residenceVersoFile;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={YOUNITED_FLOW_LOGO} alt="Younited" className="h-10 w-auto object-contain" />
          <button 
            onClick={() => navigate('/client/dashboard')}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back button */}
        {documentType && (
          <button 
            onClick={() => setDocumentType(null)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </button>
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Justificatif d'identité</h1>
        <p className="text-gray-600 mb-8">
          Veuillez télécharger votre pièce d'identité pour valider votre dossier.
        </p>

        {!documentType ? (
          /* Document Type Selection */
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Choisissez votre type de document :</h2>
            
            <Card 
              className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-purple-400"
              onClick={() => setDocumentType('id_card')}
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <CreditCard className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Carte d'identité</h3>
                  <p className="text-sm text-gray-600">Recto et verso requis</p>
                </div>
              </div>
            </Card>

            <Card 
              className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-purple-400"
              onClick={() => setDocumentType('passport')}
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <BookOpen className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Passeport</h3>
                  <p className="text-sm text-gray-600">Page avec photo</p>
                </div>
              </div>
            </Card>

            <Card 
              className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-purple-400"
              onClick={() => setDocumentType('residence_permit')}
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileText className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Titre de séjour</h3>
                  <p className="text-sm text-gray-600">Recto et verso requis</p>
                </div>
              </div>
            </Card>
          </div>
        ) : documentType === 'id_card' ? (
          /* ID Card Upload */
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Carte d'identité</h2>
            
            {/* Recto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recto (face avant) *
              </label>
              {rectoPreview ? (
                <div className="relative">
                  <img 
                    src={rectoPreview} 
                    alt="Recto" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => removeFile('recto')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {rectoFile.name}
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <Upload className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Cliquez pour télécharger</span>
                  <span className="text-xs text-gray-400 mt-1">JPG, PNG ou PDF (max 10MB)</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'recto')}
                  />
                </label>
              )}
            </div>

            {/* Verso */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verso (face arrière) *
              </label>
              {versoPreview ? (
                <div className="relative">
                  <img 
                    src={versoPreview} 
                    alt="Verso" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => removeFile('verso')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {versoFile.name}
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <Upload className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Cliquez pour télécharger</span>
                  <span className="text-xs text-gray-400 mt-1">JPG, PNG ou PDF (max 10MB)</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'verso')}
                  />
                </label>
              )}
            </div>
          </div>
        ) : documentType === 'passport' ? (
          /* Passport Upload */
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Passeport</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Page avec photo *
              </label>
              {passportPreview ? (
                <div className="relative">
                  <img 
                    src={passportPreview} 
                    alt="Passeport" 
                    className="w-full h-64 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => removeFile('passport')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {passportFile.name}
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <Upload className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Cliquez pour télécharger</span>
                  <span className="text-xs text-gray-400 mt-1">JPG, PNG ou PDF (max 10MB)</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'passport')}
                  />
                </label>
              )}
            </div>
          </div>
        ) : (
          /* Titre de séjour Upload */
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Titre de séjour</h2>
            
            {/* Recto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recto (face avant) *
              </label>
              {residenceRectoPreview ? (
                <div className="relative">
                  <img 
                    src={residenceRectoPreview} 
                    alt="Titre de séjour Recto" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => removeFile('residence_recto')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {residenceRectoFile.name}
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <Upload className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Cliquez pour télécharger</span>
                  <span className="text-xs text-gray-400 mt-1">JPG, PNG ou PDF (max 10MB)</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'residence_recto')}
                  />
                </label>
              )}
            </div>

            {/* Verso */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verso (face arrière) *
              </label>
              {residenceVersoPreview ? (
                <div className="relative">
                  <img 
                    src={residenceVersoPreview} 
                    alt="Titre de séjour Verso" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => removeFile('residence_verso')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {residenceVersoFile.name}
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <Upload className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Cliquez pour télécharger</span>
                  <span className="text-xs text-gray-400 mt-1">JPG, PNG ou PDF (max 10MB)</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'residence_verso')}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        {documentType && (
          <div className="mt-8">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit() || uploading}
              className="w-full text-white font-semibold py-4 text-base"
              style={{ backgroundColor: canSubmit() ? '#8B5CF6' : '#9CA3AF' }}
            >
              {uploading ? 'Envoi en cours...' : 'Valider et envoyer'}
            </Button>
          </div>
        )}

        {/* Info */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Important :</strong> Assurez-vous que votre document est lisible et que toutes les informations sont visibles. 
            Les documents flous ou illisibles seront refusés.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IdentityUpload;
